<template>
  <b-badge
    :variant="variant"
    :class="['ai-badge', sizeClass, customClass, { clickable: clickable && !loading && !disabled, loading: loading, disabled: disabled }]"
    :style="{ cursor: disabled ? 'not-allowed' : (loading ? 'wait' : (clickable ? 'pointer' : 'default')) }"
    v-bind="$attrs"
    @click="handleClick"
  >
    <b-spinner
      v-if="loading"
      small
      class="ai-spinner"
      :style="{ width: iconSize + 'px', height: iconSize + 'px' }"
    />
    <icon
      v-else
      :icon="icon"
      :width="iconSize"
      :height="iconSize"
      class="ai-icon"
    />
    {{ loading ? loadingText : text }}
    <span v-if="cost && !loading" class="ai-cost" :class="'ai-cost--' + size">
      <icon icon="twemoji:coin" :width="coinSize" :height="coinSize" class="ai-cost-icon" />
      {{ cost }}
    </span>
  </b-badge>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'

export default defineComponent({
  name: 'AiBadge',
  inheritAttrs: false,
  props: {
    text: {
      type: String,
      default: 'AI'
    },
    icon: {
      type: String,
      default: 'humbleicons:ai'
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['xs', 'sm', 'md', 'lg'].includes(value)
    },
    variant: {
      type: String,
      default: 'primary'
    },
    clickable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: 'Loading...'
    },
    customClass: {
      type: String,
      default: ''
    },
    cost: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['click'],
  setup(props, { emit }) {
    const sizeClass = computed(() => `ai-badge--${props.size}`)

    const iconSize = computed(() => {
      const sizes = {
        xs: 12,
        sm: 16,
        md: 20,
        lg: 24
      }
      return sizes[props.size]
    })

    const coinSize = computed(() => {
      const sizes = {
        xs: 11,
        sm: 13,
        md: 15,
        lg: 17
      }
      return sizes[props.size]
    })
    
    const handleClick = (event) => {
      if (props.clickable && !props.loading && !props.disabled) {
        emit('click', event)
      }
    }
    
    return {
      sizeClass,
      iconSize,
      coinSize,
      handleClick
    }
  }
})
</script>

<style lang="scss" scoped>
.ai-badge {
  font-weight: 600;
  border-radius: 16px;
  background: linear-gradient(135deg, #00d4ff 0%, #8709dc 100%);
  border: none;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(135, 9, 220, 0.2);
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  .ai-icon {
    margin-right: 0.25rem;
    filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.3));
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(135, 9, 220, 0.3);
    animation: shine 0.8s ease-in-out;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        transparent
      );
      animation: shineSlide 0.8s ease-in-out;
      border-radius: inherit;
    }
  }
  
  // Size variants
  &--xs {
    font-size: 0.6rem;
    padding: 0.15rem 0.3rem;
    border-radius: 8px;
    
    .ai-icon {
      margin-right: 0.15rem;
    }
  }
  
  &--sm {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
    border-radius: 10px;
    
    .ai-icon {
      margin-right: 0.2rem;
    }
  }
  
  &--md {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
  }
  
  &--lg {
    font-size: 0.85rem;
    padding: 0.35rem 0.7rem;
    border-radius: 16px;
    
    .ai-icon {
      margin-right: 0.35rem;
    }
  }
  
  // Clickable variant
  &.clickable {
    cursor: pointer;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(135, 9, 220, 0.4);
    }

    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }

  // Disabled state — visible but non-interactive (e.g. feature N/A for this context)
  &.disabled {
    opacity: 0.45;
    filter: grayscale(0.6);
    box-shadow: none;

    &:hover {
      transform: none;
      box-shadow: none;
      animation: none;

      &::after {
        display: none;
      }
    }
  }

  // Cost indicator
  .ai-cost {
    display: inline-flex;
    align-items: center;
    margin-left: 0.2rem;
    padding: 0 0.25rem;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.2);
    font-weight: 700;
    line-height: 1;
    gap: 1px;

    .ai-cost-icon {
      flex-shrink: 0;
    }

    &--xs {
      font-size: 0.65rem;
      padding: 1px 4px;
    }

    &--sm {
      font-size: 0.7rem;
      padding: 2px 5px;
    }

    &--md {
      font-size: 0.8rem;
      padding: 2px 6px;
    }

    &--lg {
      font-size: 0.85rem;
      padding: 3px 7px;
    }
  }

  // Loading state
  &.loading {
    opacity: 0.9;
    animation: aiPulse 1.5s ease-in-out infinite;
    pointer-events: none;

    .ai-spinner {
      margin-right: 0.25rem;
      border-width: 1.5px;
    }
  }
}

// Responsive adjustments
@media (max-width: 576px) {
  .ai-badge {
    &--md {
      font-size: 0.65rem;
      padding: 0.2rem 0.4rem;
    }
    
    &--lg {
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
    }
  }
}

// Keyframe animations for the shining effect
@keyframes shine {
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 100% 0;
  }
}

@keyframes shineSlide {
  0% {
    left: -100%;
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}

@keyframes aiPulse {
  0%, 100% {
    box-shadow: 0 2px 4px rgba(135, 9, 220, 0.2);
  }
  50% {
    box-shadow: 0 2px 12px rgba(135, 9, 220, 0.5);
  }
}
</style>